import { BrandLockup } from "../Logo";
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { Home, UtensilsCrossed, History, MessageCircle, User, LayoutDashboard, Wallet, CalendarClock, FileCheck, FileWarning, ListOrdered, BookOpen, BarChart3, ShieldCheck, Newspaper, Users, BellRing, LogOut, Menu, Bell, ChevronRight, ShoppingBag, BadgeCheck, Store, Tag, Star, MessageSquareWarning, UserCog, Banknote, Sun, Moon, ClipboardList, XCircle, Car, Images, MapPin, ShieldAlert, Smartphone, Activity, Siren, Receipt, PackageX, FileText } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { driverCaps } from "../../lib/driver";
import { useTheme } from "../../context/ThemeContext";
import { useCart } from "../../context/CartContext";
import { useLocationCtx } from "../../context/LocationContext";
import LocationGate from "./LocationGate";
import SosButton from "./SosButton";
import SosRingListener from "./SosRingListener";
import RestaurantOrderRing from "./RestaurantOrderRing";
import DriverRideRing from "./DriverRideRing";
import ReConsentGate from "./ReConsentGate";
import { chatApi, publicApi, notificationsApi, driverApi, restaurantApi, adminApi, errorMessage } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Sheet, SheetContent } from "../ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { InstallButton } from "./InstallApp";
import AddEmailReminder from "./AddEmailReminder";

const MENUS = {
  rider: [
    { to: "/app/home", label: "Home", icon: Home, hint: "Book a ride" },
    { to: "/app/food", label: "Food", icon: UtensilsCrossed },
    { to: "/app/private-cars", label: "Private cars", icon: Car },
    { to: "/app/rides", label: "Ride history", icon: History },
    { to: "/app/chat", label: "Messages", icon: MessageCircle },
    { to: "/app/profile", label: "Profile", icon: User },
  ],
  driver: [
    { to: "/app/driver", label: "Dashboard", icon: LayoutDashboard },
    { to: "/app/deliveries", label: "Deliveries", icon: UtensilsCrossed, cap: "food" },
    { to: "/app/earnings", label: "Earnings", icon: Wallet },
    { to: "/app/payouts", label: "Payouts", icon: Banknote },
    { to: "/app/schedule", label: "Schedule", icon: CalendarClock, cap: "rides" },
    { to: "/app/private-car-jobs", label: "Private car jobs", icon: Car, cap: "privatecar" },
    { to: "/app/chat", label: "Messages", icon: MessageCircle },
    { to: "/app/profile", label: "Profile", icon: User },
  ],
  restaurant: [
    { to: "/app/restaurant/orders", label: "Orders", icon: ListOrdered, perm: "orders" },
    { to: "/app/restaurant/menu", label: "Menu", icon: BookOpen, perm: "menu" },
    { to: "/app/restaurant/info", label: "Restaurant info & KYC", icon: Store, perm: "hours" },
    { to: "/app/restaurant/discounts", label: "Discounts", icon: Tag, perm: "discounts" },
    { to: "/app/restaurant/ratings", label: "Ratings", icon: Star, perm: "ratings" },
    { to: "/app/restaurant/complaints", label: "Complaints", icon: MessageSquareWarning, perm: "ratings" },
    { to: "/app/restaurant/sales", label: "Sales report", icon: BarChart3, perm: "reports" },
    { to: "/app/restaurant/reject-reasons", label: "Reject reasons", icon: XCircle, perm: "reports" },
    { to: "/app/payouts", label: "Payouts", icon: Banknote, ownerOnly: true },
    { to: "/app/restaurant/managers", label: "Managers", icon: UserCog, ownerOnly: true },
    { to: "/app/restaurant/analytics", label: "Analytics", icon: BarChart3, perm: "reports" },
    { to: "/app/chat", label: "Messages", icon: MessageCircle },
    { to: "/app/profile", label: "Profile", icon: User },
  ],
  admin: [
    { to: "/app/admin/verify/restaurants", label: "Restaurant Verification", icon: Store, badgeKey: "restaurant" },
    { to: "/app/admin/verify/commercial-drivers", label: "Taxi Verification", icon: BadgeCheck, badgeKey: "commercial_driver" },
    { to: "/app/admin/verify/delivery-partners", label: "Delivery Verification", icon: ShoppingBag, badgeKey: "delivery_partner" },
    { to: "/app/admin/verify/private-cars", label: "Private Car Verification", icon: Car, badgeKey: "private_car" },
    { to: "/app/admin/verify/docs-pending", label: "Documents pending", icon: FileWarning },
    { to: "/app/admin/address-changes", label: "Address changes", icon: MapPin },
    { to: "/app/admin/rekyc", label: "Re-KYC expiry", icon: FileCheck },
    { to: "/app/admin/live-orders", label: "Live orders", icon: Activity },
    { to: "/app/admin/past-orders", label: "Past orders", icon: History },
    { to: "/app/admin/no-partner", label: "No-driver orders", icon: PackageX },
    { to: "/app/admin/sos", label: "SOS alerts", icon: Siren, sosBadge: true },
    { to: "/app/admin/oncall", label: "On-call & hours", icon: CalendarClock },
    { to: "/app/admin/tasks", label: "Internal tasks", icon: ClipboardList },
    { to: "/app/admin/reports", label: "Reports", icon: ShieldAlert },
    { to: "/app/admin/fee-disputes", label: "Fee disputes", icon: Receipt },
    { to: "/app/admin/photo-moderation", label: "Photo moderation", icon: Images },
    { to: "/app/admin/stories", label: "Stories CMS", icon: Newspaper },
    { to: "/app/admin/users", label: "Users", icon: Users },
    { to: "/app/admin/restaurants", label: "Restaurants", icon: UtensilsCrossed },
    { to: "/app/admin/team", label: "Team", icon: UserCog },
    { to: "/app/admin/payouts", label: "Payouts", icon: Banknote },
    { to: "/app/admin/gst-invoices", label: "GST invoices", icon: FileText },
    { to: "/app/admin/financial-report", label: "Financial report", icon: BarChart3 },
    { to: "/app/admin/notifications", label: "Notifications status", icon: BellRing },
    { to: "/app/admin/app-version", label: "App version", icon: Smartphone },
    { to: "/app/chat", label: "Support messages", icon: MessageCircle },
    { to: "/app/profile", label: "Profile", icon: User },
  ],
  manager: [
    { to: "/app/admin/live-orders", label: "Live orders", icon: Activity },
    { to: "/app/admin/past-orders", label: "Past orders", icon: History },
    { to: "/app/admin/no-partner", label: "No-driver orders", icon: PackageX },
    { to: "/app/admin/sos", label: "SOS alerts", icon: Siren, sosBadge: true },
    { to: "/app/admin/oncall", label: "On-call & hours", icon: CalendarClock },
    { to: "/app/admin/verify/restaurants", label: "Restaurant Verification", icon: Store, badgeKey: "restaurant" },
    { to: "/app/admin/verify/commercial-drivers", label: "Taxi Verification", icon: BadgeCheck, badgeKey: "commercial_driver" },
    { to: "/app/admin/verify/delivery-partners", label: "Delivery Verification", icon: ShoppingBag, badgeKey: "delivery_partner" },
    { to: "/app/admin/verify/private-cars", label: "Private Car Verification", icon: Car, badgeKey: "private_car" },
    { to: "/app/admin/verify/docs-pending", label: "Documents pending", icon: FileWarning },
    { to: "/app/admin/address-changes", label: "Address changes", icon: MapPin },
    { to: "/app/admin/rekyc", label: "Re-KYC expiry", icon: FileCheck },
    { to: "/app/admin/tasks", label: "Internal tasks", icon: ClipboardList },
    { to: "/app/admin/fee-disputes", label: "Fee disputes", icon: Receipt },
    { to: "/app/admin/users", label: "Users", icon: Users },
    { to: "/app/admin/restaurants", label: "Restaurants", icon: UtensilsCrossed },
    { to: "/app/admin/payouts", label: "Payouts", icon: Banknote },
    { to: "/app/admin/team", label: "Team", icon: UserCog },
    { to: "/app/chat", label: "Support messages", icon: MessageCircle },
    { to: "/app/profile", label: "Profile", icon: User },
  ],
  agent: [
    { to: "/app/admin/sos", label: "SOS alerts", icon: Siren, sosBadge: true },
    { to: "/app/admin/oncall", label: "On-call & hours", icon: CalendarClock },
    { to: "/app/admin/tasks", label: "Internal tasks", icon: ClipboardList },
    { to: "/app/admin/restaurants", label: "Restaurants", icon: UtensilsCrossed },
    { to: "/app/admin/payouts", label: "Payouts", icon: Banknote },
    { to: "/app/chat", label: "Support messages", icon: MessageCircle },
    { to: "/app/profile", label: "Profile", icon: User },
  ],
};
MENUS.customer = MENUS.rider;

const LABELS = {
  app: "Home", home: "Book a ride", food: "Food", checkout: "Checkout", orders: "Orders", rides: "Ride history", chat: "Messages", profile: "Profile",
  driver: "Dashboard", earnings: "Earnings", schedule: "Schedule", kyc: "KYC status", restaurant: "Restaurant", menu: "Menu", analytics: "Analytics", info: "Restaurant info & KYC", deliveries: "Deliveries", discounts: "Discounts", ratings: "Ratings", complaints: "Complaints", sales: "Sales report", managers: "Managers", payouts: "Payouts",
  admin: "Admin", verifications: "Verifications", verify: "Verification", restaurants: "Restaurant", "commercial-drivers": "Taxi", "delivery-partners": "Delivery", "private-cars": "Private car", stories: "Stories CMS", users: "Users", notifications: "Notifications status", permits: "Taxi permits", "photo-moderation": "Photo moderation",
};

const ROLE_LABEL = { rider: "Customer", customer: "Customer", driver: "Driver partner", restaurant: "Restaurant partner", admin: "Administrator", manager: "Manager", agent: "Internal agent" };

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "K";
}

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [verifCounts, setVerifCounts] = useState({});
  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "manager") return;
    let on = true;
    const fetchCounts = () => import("../../lib/api").then(({ adminApi }) => adminApi.verificationCounts()).then((c) => { if (on) setVerifCounts(c || {}); }).catch(() => {});
    fetchCounts();
    const t = setInterval(fetchCounts, 30000);
    return () => { on = false; clearInterval(t); };
  }, [user?.role]);
  const [chatUnread, setChatUnread] = useState(0);
  useEffect(() => {
    if (!user) return undefined;
    let on = true;
    const load = () => chatApi.summary().then((s) => { if (on) setChatUnread(s?.unread_total || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { on = false; clearInterval(t); };
  }, [user?.user_id]);
  const rawMenu = MENUS[user?.role] || MENUS.rider;
  const perms = user?.is_manager ? user?.permissions : null; // null => owner/grandfathered => all
  const caps = user?.role === "driver" ? driverCaps(user) : null;
  const menu = rawMenu.filter((m) => {
    // Driver nav: show only items relevant to this driver's job role.
    if (caps && m.cap) {
      if (m.cap === "food" && !caps.canFood) return false;
      if (m.cap === "rides" && !caps.canRides) return false;
      if (m.cap === "privatecar" && !caps.isPrivateCar) return false;
    }
    if (!user?.is_manager) return true;
    if (m.ownerOnly) return false;
    if (m.perm && perms !== null && perms !== undefined && !perms.includes(m.perm)) return false;
    return true;
  });
  const doLogout = () => {
    logout();
    navigate("/");
  };
  return (
    <div className="flex h-full flex-col bg-surface-50/95 backdrop-blur">
      <div className="flex h-[72px] items-center border-b border-surface-300 px-5">
        <Link to="/" className="flex items-center" onClick={onNavigate} data-testid="sidebar-brand">
          <BrandLockup />
        </Link>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-5 kt-scrollbar" aria-label="App navigation">
        <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-muted">{ROLE_LABEL[user?.role] || "Menu"}</p>
        {menu.map((m) => (
          <NavLink
            key={m.to}
            to={m.to}
            onClick={onNavigate}
            className={({ isActive }) => `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-brand text-white shadow-sm" : "text-ink-soft hover:bg-surface-200 hover:text-ink"}`}
            data-testid={`sidebar-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <m.icon className="h-[18px] w-[18px] shrink-0" />
            <span className="flex-1">{m.label}</span>
            {m.badgeKey && verifCounts[m.badgeKey] > 0 && <span className="ml-auto rounded-full bg-brand px-2 py-0.5 text-[11px] font-bold text-white group-[.bg-brand]:bg-white group-[.bg-brand]:text-brand" data-testid={`sidebar-badge-${m.badgeKey}`}>{verifCounts[m.badgeKey]}</span>}
            {m.to === "/app/chat" && chatUnread > 0 && <span className="ml-auto rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-bold text-white group-[.bg-brand]:bg-white group-[.bg-brand]:text-red-600" data-testid="sidebar-badge-chat">{chatUnread > 9 ? "9+" : chatUnread}</span>}
          </NavLink>
        ))}
        {user?.role === "restaurant" && <InstallButton className="px-0 pt-4" />}
      </nav>
      <div className="border-t border-surface-300 p-4">
        <div className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-sm">
          <Avatar className="h-10 w-10"><AvatarFallback className="bg-graphite text-sm font-semibold text-white">{initials(user?.name)}</AvatarFallback></Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="truncate text-xs text-ink-muted">{user?.email?.endsWith("@phone.local") ? user?.phone : user?.email}</p>
          </div>
          <button onClick={doLogout} className="rounded-lg p-2 text-ink-muted transition-colors hover:bg-surface-200 hover:text-brand" title="Log out" aria-label="Log out" data-testid="sidebar-logout">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((p, i) => ({ label: LABELS[p] || (p.length > 14 ? `${p.slice(0, 14)}…` : p), to: `/${parts.slice(0, i + 1).join("/")}` }));
  return (
    <nav className="flex min-w-0 items-center gap-1 text-sm" aria-label="Breadcrumb" data-testid="breadcrumbs">
      {crumbs.map((c, i) => (
        <span key={c.to} className="flex min-w-0 items-center gap-1">
          {i > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-muted" />}
          {i === crumbs.length - 1 ? (
            <span className="truncate font-semibold text-ink">{c.label}</span>
          ) : (
            <Link to={c.to} className="truncate text-ink-muted hover:text-brand">{c.label}</Link>
          )}
        </span>
      ))}
    </nav>
  );
}

function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ unread_total: 0, chats: [] });
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => {
      chatApi.summary().then((s) => alive && setSummary(s)).catch(() => {});
      notificationsApi.list().then((n) => alive && setNotifs(n)).catch(() => {});
    };
    load();
    publicApi.notificationsStatus().then((s) => alive && setStatus(s)).catch(() => {});
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const unread = (summary.unread_total || 0) + (notifs.unread || 0);
  const markAll = () => { notificationsApi.markAllRead().then(() => setNotifs((p) => ({ ...p, unread: 0 }))).catch(() => {}); };
  const role = user?.role;
  const isStaff = ["admin", "manager", "agent"].includes(role);
  const notifTarget = (n) => {
    const k = n.kind;
    if (isStaff) {
      if (k === "task") return "/app/admin/tasks";
      if (k === "report") return "/app/admin/reports";
      if (k === "sos") return "/app/admin/sos";
      if (k === "verification" || k === "kyc_submitted") return "/app/admin/verify/restaurants";
      if (k === "ride" || k === "food") return "/app/admin/live-orders";
      return null;
    }
    if (k === "kyc" || k === "warning") return "/app/profile";
    if (k === "report") return "/app/profile";
    if (k === "ride") return role === "driver" ? "/app/driver" : "/app/rides";
    if (k === "food") return role === "restaurant" ? "/app/restaurant/orders" : "/app/orders";
    if (k === "private_car") return role === "driver" ? "/app/private-car-jobs" : "/app/private-cars";
    if (k === "task") return "/app/driver";
    return null;
  };
  const openNotif = (n) => {
    const to = notifTarget(n);
    if (!n.read) {
      notificationsApi.markRead(n.notification_id).catch(() => {});
      setNotifs((p) => ({ ...p, unread: Math.max(0, (p.unread || 0) - 1), notifications: p.notifications.map((x) => x.notification_id === n.notification_id ? { ...x, read: true } : x) }));
    }
    if (to) navigate(to);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative rounded-xl p-2 text-ink-soft transition-colors hover:bg-surface-200 hover:text-ink" aria-label="Notifications" data-testid="topbar-notifications">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-surface-300 px-4 py-3">
          <p className="text-sm font-semibold text-ink">Notifications</p>
          {notifs.unread > 0 && <button onClick={markAll} className="text-xs font-semibold text-brand hover:underline" data-testid="notifs-mark-all">Mark all read</button>}
        </div>
        <div className="max-h-96 overflow-y-auto p-2 kt-scrollbar" data-testid="notifs-list">
          {notifs.notifications?.slice(0, 15).map((n) => {
            const clickable = !!notifTarget(n);
            return (
              <button
                key={n.notification_id}
                type="button"
                onClick={() => openNotif(n)}
                disabled={!clickable && n.read}
                className={`block w-full rounded-xl px-3 py-2.5 text-left ${n.read ? "" : "bg-brand-50"} ${clickable ? "cursor-pointer hover:bg-surface-100" : "cursor-default"}`}
                data-testid={`notif-${n.notification_id}`}
              >
                <p className="text-sm font-medium text-ink">{n.title}</p>
                <p className="text-xs text-ink-soft">{n.body}</p>
                {clickable && <p className="mt-0.5 text-[11px] font-semibold text-brand">View →</p>}
              </button>
            );
          })}
          {summary.chats?.length ? summary.chats.map((c) => (
            <Link key={c.chat_id} to={`/app/chat/${c.chat_id}`} className="flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-surface-100">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-graphite-50 text-graphite"><MessageCircle className="h-4 w-4" /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-ink">{c.title || c.other_name || c.chat_type || c.chat_id}</span>
                <span className="block truncate text-xs text-ink-muted">{c.last_message?.text || c.last_text || "Open conversation"}</span>
              </span>
              {c.unread > 0 && <span className="rounded-full bg-brand px-1.5 text-[10px] font-bold text-white">{c.unread}</span>}
            </Link>
          )) : null}
          {!notifs.notifications?.length && !summary.chats?.length && (
            <p className="px-3 py-6 text-center text-sm text-ink-muted">You are all caught up.</p>
          )}
        </div>
        {status && (
          <div className="border-t border-surface-300 px-4 py-2.5 text-[11px] text-ink-muted">
            Channels: email {status.email ? "on" : "off"} · SMS {status.sms ? "on" : "off"}{status.dev_mock ? " · test mode" : ""}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function OnlineToggle() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;
  const isDriver = role === "driver";
  const isRestaurant = role === "restaurant";
  const isStaff = role === "manager" || role === "agent";
  const [online, setOnline] = useState(isDriver ? !!user?.is_online : isStaff ? !!user?.staff_online : false);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(isDriver || isStaff);
  const [showOfflineDialog, setShowOfflineDialog] = useState(false);
  const [offReason, setOffReason] = useState(null);
  const [offReopen, setOffReopen] = useState(false);
  const OFFLINE_REASONS = ["Too busy", "Closing early", "Out of stock", "Short break"];

  useEffect(() => {
    if (isRestaurant) {
      restaurantApi.mine().then((r) => { setOnline(!!r?.is_open); setReady(true); }).catch(() => setReady(true));
    } else if (isDriver) {
      setOnline(!!user?.is_online);
      setReady(true);
    } else if (isStaff) {
      setOnline(!!user?.staff_online);
      setReady(true);
    }
  }, [isRestaurant, isDriver, isStaff]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isDriver && !isRestaurant && !isStaff) return null;

  const getGeoFix = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { timeout: 8000, enableHighAccuracy: false },
    );
  });

  const applyRestaurant = async (open, opts) => {
    setBusy(true);
    try {
      await restaurantApi.setStatus(open, opts);
      setOnline(open);
      toast.success(open ? "Restaurant is open" : "Restaurant is closed");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setBusy(false); }
  };

  const toggle = async () => {
    if (busy || !ready) return;
    const next = !online;
    // Restaurant going OFFLINE → ask for a quick reason + optional auto-reopen first.
    if (isRestaurant) {
      if (!next) { setShowOfflineDialog(true); return; }
      await applyRestaurant(true);
      return;
    }
    setBusy(true);
    try {
      if (isDriver) {
        if (next) {
          const fix = await getGeoFix();
          if (!fix) { toast.error("Turn on location access in your browser to go online."); setBusy(false); return; }
          await driverApi.postLocation(fix.lat, fix.lng);
          await driverApi.online(next);
        } else {
          await driverApi.online(next);
        }
        setUser({ ...user, is_online: next });
      }
      else if (isStaff) { const { adminApi } = await import("../../lib/api"); const res = await adminApi.setStaffOnline(next); setUser(res.user || { ...user, staff_online: next }); }
      setOnline(next);
      toast.success(next ? (isDriver ? "You're online — accepting jobs" : "You're online — receiving work") : "You're offline");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (isDriver && d && typeof d === "object" && d.code === "phone_required") {
        toast.error(d.message || "Add & verify your phone number to go online so customers can reach you.");
        navigate("/app/profile?verifyPhone=1");
      } else { toast.error(errorMessage(e)); }
    } finally { setBusy(false); }
  };

  return (
    <>
    <button
      type="button"
      onClick={toggle}
      disabled={busy || !ready}
      aria-pressed={online}
      title={online ? "Tap to go offline" : "Tap to go online"}
      className={`flex items-center gap-2 rounded-full border px-2.5 py-1.5 text-sm font-semibold transition-colors disabled:opacity-60 ${online ? "border-green-500/30 bg-green-100 text-green-700 dark:bg-green-500/15" : "border-surface-300 bg-surface-100 text-ink-soft"}`}
      data-testid="header-online-toggle"
    >
      <span className={`relative h-5 w-9 rounded-full transition-colors ${online ? "bg-green-500" : "bg-ink-muted/50"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${online ? "left-[18px]" : "left-0.5"}`} />
      </span>
      <span className="hidden sm:inline">{busy ? "…" : online ? "Online" : "Offline"}</span>
    </button>
    {showOfflineDialog && (
      <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/45 sm:items-center" onClick={() => setShowOfflineDialog(false)} data-testid="restaurant-offline-dialog">
        <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-surface-50 p-5 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-lg font-extrabold text-ink">Switch restaurant offline</h3>
          <p className="mt-0.5 text-sm text-ink-soft">Pick a reason — your team gets notified.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {OFFLINE_REASONS.map((r) => {
              const sel = offReason === r;
              return (
                <button key={r} type="button" onClick={() => setOffReason(sel ? null : r)}
                  data-testid={`offline-reason-${r.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors ${sel ? "border-brand-primary bg-brand-primary/10 text-brand-primary" : "border-surface-300 bg-surface-100 text-ink-soft"}`}>
                  {r}
                </button>
              );
            })}
          </div>
          <label className="mt-5 flex cursor-pointer items-start gap-3" data-testid="offline-reopen-toggle">
            <input type="checkbox" checked={offReopen} onChange={(e) => setOffReopen(e.target.checked)} className="mt-0.5 h-5 w-5 accent-brand-primary" />
            <span>
              <span className="block text-sm font-semibold text-ink">Reopen automatically in 30 min</span>
              <span className="block text-xs text-ink-soft">We'll switch you back online and tell your team.</span>
            </span>
          </label>
          <button type="button" data-testid="offline-dialog-confirm" disabled={busy}
            className="mt-5 w-full rounded-full bg-red-500 py-3 text-sm font-extrabold text-white transition-colors hover:bg-red-600 disabled:opacity-60"
            onClick={async () => {
              setShowOfflineDialog(false);
              await applyRestaurant(false, { offline_reason: offReason || undefined, reopen_in_min: offReopen ? 30 : undefined });
              setOffReason(null); setOffReopen(false);
            }}>
            Switch offline
          </button>
          <button type="button" data-testid="offline-dialog-cancel" className="mt-2 w-full py-2.5 text-sm font-semibold text-ink-soft" onClick={() => setShowOfflineDialog(false)}>
            Stay online
          </button>
        </div>
      </div>
    )}
    </>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      className="rounded-xl p-2 text-ink-soft transition-colors hover:bg-surface-200 hover:text-ink"
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      data-testid="theme-toggle"
    >
      {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </button>
  );
}

function LocationChip() {
  const { location, openPrompt } = useLocationCtx();
  return (
    <button onClick={openPrompt} className="hidden max-w-[200px] items-center gap-1.5 rounded-xl px-2.5 py-2 text-sm text-ink-soft transition-colors hover:bg-surface-200 hover:text-ink sm:flex" data-testid="topbar-location">
      <MapPin className="h-4 w-4 shrink-0 text-brand" />
      <span className="truncate font-medium">{location?.label || "Set location"}</span>
    </button>
  );
}

let locationPromptedThisLoad = false;

export default function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const { count } = useCart();
  const { pathname } = useLocation();
  const { closePrompt, ensureLocation, location: ctxLocation } = useLocationCtx();
  const isCustomer = useMemo(() => ["rider", "customer"].includes(user?.role), [user]);
  useEffect(() => setMobileOpen(false), [pathname]);

  // On app open, auto-detect the customer's location (device GPS). If that fails/denied, the
  // manual "set location" prompt opens. Never while mid-task (checkout / live order / trip),
  // where a modal would block the page's own actions.
  useEffect(() => {
    const onTaskRoute = pathname.startsWith("/app/orders/") || pathname.startsWith("/app/checkout") || pathname === "/app/trip";
    if (onTaskRoute) { closePrompt(); return; }
    if (isCustomer && !ctxLocation && !locationPromptedThisLoad) {
      locationPromptedThisLoad = true;
      ensureLocation();
    }
  }, [isCustomer, ensureLocation, closePrompt, pathname, ctxLocation]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-surface-100 to-amber-50 text-ink dark:from-[#17130F] dark:via-[#17130F] dark:to-[#1C1712]">
      {isCustomer && <LocationGate />}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[260px] border-r border-surface-300 lg:block" data-testid="sidebar">
        <SidebarContent />
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[280px] p-0">
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-30 flex h-[64px] items-center gap-3 border-b border-surface-300 kt-glass px-4 sm:px-6 lg:px-8" data-testid="topbar">
          <button className="rounded-xl p-2 text-ink lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu" data-testid="topbar-menu"><Menu className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1"><Breadcrumbs /></div>
          <OnlineToggle />
          {isCustomer && <LocationChip />}
          <ThemeToggle />
          {isCustomer && (
            <Button asChild variant="ghost" className="relative rounded-xl text-ink-soft hover:bg-surface-200" data-testid="topbar-cart">
              <Link to="/app/checkout" aria-label="Cart">
                <ShoppingBag className="h-5 w-5" />
                {count > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-graphite px-1 text-[10px] font-bold text-white">{count}</span>}
              </Link>
            </Button>
          )}
          {["rider", "customer", "driver"].includes(user?.role) && <SosButton variant="icon" />}
          <NotificationsBell />
        </header>
        <main className="px-4 py-8 sm:px-6 lg:px-10" data-testid="app-main">
          <div className="mx-auto max-w-[1400px]"><Outlet /></div>
        </main>
      </div>
      {["admin", "manager", "agent"].includes(user?.role) && <SosRingListener />}
      {user?.role === "restaurant" && <RestaurantOrderRing />}
      {user?.role === "driver" && <DriverRideRing />}
      <StaffHeartbeat role={user?.role} />
      <AddEmailReminder />
      <ReConsentGate />
    </div>
  );
}

function StaffHeartbeat({ role }) {
  useEffect(() => {
    if (!["admin", "manager", "agent"].includes(role)) return;
    const ping = () => { adminApi.staffHeartbeat().catch(() => {}); };
    ping();
    const t = setInterval(ping, 60000);
    return () => clearInterval(t);
  }, [role]);
  return null;
}
